/**
 * PF v2 (revamp) — purchase-session create mutation.
 *
 * Wraps `POST /workspace/{workspaceId}/sessions` (operation `createSessionAsClient`)
 * against the *deployed* dev contract. The v2 session REPLACES the legacy
 * purchase-flow session inside the revamp — its `sessionId` is stored in the
 * existing `draft.sessionId` store field (see `useEnsurePfV2Session`) and flows
 * onto the catalogue request + the order placement body.
 *
 * Body is role-dependent:
 *   - client → `{ realEstatePropertyId }`
 *   - admin  → `{ realEstatePropertyId, email }`  (email = the client the admin acts for)
 *
 * BE 422s if the property lacks `postalCode` / area — the caller enforces a
 * property-completeness gate before invoking this (see `usePropertyCompleteness`).
 */

import { useMutation } from '@tanstack/react-query'

import type {
  CreateOrderSessionAdminV2DTO,
  CreateOrderSessionV2DTO,
  PurchaseSessionV2DTO
} from 'models/purchaseFlowRevamp'
import { useAPIV2 } from 'utils/API'
import { useAuth0 } from 'utils/auth'

export enum QueryKeys {
  PF_V2_SESSION = 'PF_V2_SESSION',
}

export enum Endpoints {
  /** POST — opens a v2 purchase session for a property (client/admin). */
  CREATE_SESSION = '/workspace/{workspaceId}/sessions',
}

/** Variables for the create-session mutation. */
export interface CreatePfV2SessionVariables {
  /** Workspace the session is opened in (client: own property workspace; admin: target). */
  workspaceId: string
  /** Property the order is for. Must reference a property in the workspace. */
  realEstatePropertyId: string
  /** Admin-only: email of the client the admin opens the session for. Omit for client role. */
  email?: string | null
}

/**
 * Mutation hook — creates a PF v2 purchase session.
 *
 * The admin body includes `email`; the client body omits it.
 */
export function useCreatePfV2Session() {
  const api = useAPIV2<Endpoints>()
  const { roles } = useAuth0()

  return useMutation<PurchaseSessionV2DTO, unknown, CreatePfV2SessionVariables>({
    mutationKey: [QueryKeys.PF_V2_SESSION],
    mutationFn: ({ workspaceId, realEstatePropertyId, email }) => {
      // Admin sends `email`; client omits it. Both carry `realEstatePropertyId`.
      const body: CreateOrderSessionV2DTO = roles.isAdmin && email
        ? ({ realEstatePropertyId, email } satisfies CreateOrderSessionAdminV2DTO)
        : { realEstatePropertyId }

      return api.post<PurchaseSessionV2DTO>(
        Endpoints.CREATE_SESSION,
        { workspaceId },
        body,
        false
      )
    },
  })
}
